import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Gift, ArrowRight, ShieldCheck, Lock, X, Check, TrendingDown, TrendingUp,
  Link2, ShoppingBag, Wallet, BellRing, Megaphone, CalendarSync, Users,
  CheckCircle2, Plus, MessageCircle, Star, Building2, User, Mail, Phone as PhoneIcon,
} from 'lucide-react';
import { LuInstagram } from 'react-icons/lu';
import { usuarioService } from '../services/api';
import { PRECOS, PLANOS_VENDAVEIS, TRIAL_PADRAO } from '../config/planos';

const fmt = (valor) => valor.toFixed(2).replace('.', ',');

const scrollToCadastro = (e) => {
  e.preventDefault();
  document.getElementById('cadastro')?.scrollIntoView({ behavior: 'smooth' });
};

// Bullets de venda por plano (mesmo padrão de LandingPage.js — números vêm de PRECOS, texto é
// específico dessa página de conversão, mais curto pra caber nos 3 cards lado a lado).
const BULLETS_CONVERSAO = {
  COMUM: [
    'Agendamentos e clientes ilimitados',
    'Agenda pública com link próprio',
    'Controle Financeiro completo',
    '1 Google Agenda vinculada',
  ],
  PREMIUM: [
    'Tudo do Comum, e mais:',
    'Robô de WhatsApp 24h conectado',
    'Cupons, Fidelidade e Aniversário automáticos',
    'Lembretes individuais e em massa',
  ],
  EMPRESARIAL: [
    'Tudo do Premium, e mais:',
    'Gestão de Equipe (vários profissionais)',
    'Até 5 números de WhatsApp',
    'Uma Google Agenda por profissional',
  ],
};

const RECURSOS = [
  {
    icone: MessageCircle,
    cor: 'from-[#1883FF] to-[#6D28D9]',
    sombra: 'shadow-blue-100',
    titulo: 'Robô de WhatsApp 24 horas',
    descricao: 'Conecte seu número e o assistente atende, agenda, remarca e cancela sozinho — de madrugada, no fim de semana, o dia inteiro.',
    bullets: ['Fluxo de conversa natural', 'Entende horário digitado de qualquer jeito', 'Confirma antes de gravar', 'Reconhece cliente recorrente'],
  },
  {
    icone: Link2,
    cor: 'from-purple-500 to-purple-700',
    sombra: 'shadow-purple-100',
    titulo: 'Agenda pública com link próprio',
    descricao: 'Sua página de agendamento (agendasincro.com.br/sua-marca), com suas cores, endereço e diferenciais — sem precisar chamar no WhatsApp.',
    bullets: ['Link com a sua marca', 'Cores personalizadas', 'Wi-Fi, estacionamento e mais', 'Funciona em qualquer celular'],
  },
  {
    icone: ShoppingBag,
    cor: 'from-amber-500 to-amber-700',
    sombra: 'shadow-amber-100',
    titulo: 'Catálogo multi-serviço',
    descricao: 'Cadastre cada serviço com preço e duração. O cliente escolhe um ou vários numa mesma marcação e o sistema calcula o horário certo.',
    bullets: ['Preço e duração por serviço', 'Vários serviços numa marcação', 'Cálculo automático de horário', 'Cupom de desconto por serviço'],
  },
  {
    icone: Wallet,
    cor: 'from-emerald-500 to-emerald-700',
    sombra: 'shadow-emerald-100',
    titulo: 'Formas de pagamento configuráveis',
    descricao: 'O cliente escolhe dinheiro, Pix, crédito ou débito ao agendar. Você decide se repassa a taxa do cartão ou absorve o custo.',
    bullets: ['Dinheiro, Pix, crédito e débito', 'Taxa de cartão configurável', 'Repassar ou absorver o custo', 'Refletido direto no Financeiro'],
  },
  {
    icone: BellRing,
    cor: 'from-red-500 to-red-700',
    sombra: 'shadow-red-100',
    titulo: 'Lembretes automáticos e avulsos',
    descricao: 'Lembrete antes de cada atendimento, mais lembretes avulsos individuais ou em massa — com modelos de mensagem salvos pra reusar.',
    bullets: ['Lembrete automático pré-atendimento', 'Envio avulso individual ou em massa', 'Modelos de mensagem salvos', 'Aviso de lembrete pendente'],
  },
  {
    icone: Megaphone,
    cor: 'from-pink-500 to-pink-700',
    sombra: 'shadow-pink-100',
    titulo: 'Marketing automático',
    descricao: 'Cupons de desconto, cartão de fidelidade por pontos e mensagem de aniversário — tudo disparado sozinho pelo WhatsApp.',
    bullets: ['Cupons de desconto', 'Fidelidade por pontos', 'Aniversário automático', 'Base de clientes é sua'],
  },
  {
    icone: CalendarSync,
    cor: 'from-cyan-500 to-cyan-700',
    sombra: 'shadow-cyan-100',
    titulo: 'Sincronização com Google Agenda',
    descricao: 'O robô consulta seus horários livres em tempo real e bloqueia automaticamente o que já está ocupado — sem conflito, sem sobreposição.',
    bullets: ['Bloqueio automático de horários', 'Consulta em tempo real', 'Não altera compromissos antigos', 'Você mantém o controle total'],
  },
  {
    icone: Users,
    cor: 'from-slate-600 to-slate-900',
    sombra: 'shadow-slate-200',
    titulo: 'Gestão de equipe',
    descricao: 'Cadastre os profissionais do seu negócio, vincule a Google Agenda de cada um e conecte números extras de WhatsApp.',
    bullets: ['Vários profissionais', 'Agenda Google por profissional', 'Até 5 números de WhatsApp', 'Ideal pra salões e clínicas'],
  },
];

const FAQS = [
  {
    pergunta: 'Preciso cadastrar cartão de crédito para testar?',
    resposta: 'Não. Os 7 dias grátis são liberados só com nome, e-mail e telefone. Não pedimos cartão no cadastro, e nada é cobrado automaticamente ao fim do teste.',
  },
  {
    pergunta: 'Preciso instalar algum aplicativo?',
    resposta: 'Não. O Agenda Sincro funciona 100% no navegador. Pra conectar seu WhatsApp, basta ler um QR Code, exatamente como no WhatsApp Web.',
  },
  {
    pergunta: 'Posso usar meu próprio número de WhatsApp?',
    resposta: 'Sim. Você conecta o número da sua empresa lendo o QR Code (ou digitando o código de pareamento) no painel. As mensagens saem do seu próprio número.',
  },
  {
    pergunta: 'Eu perco o controle da minha agenda?',
    resposta: 'De forma alguma. O robô só consulta e preenche as vagas livres do seu Google Agenda. Você pode bloquear horários ou intervir na conversa a qualquer momento.',
  },
  {
    pergunta: 'Existe multa se eu quiser cancelar?',
    resposta: 'Não existe fidelidade nem multa de cancelamento. Você assina quando quiser continuar depois do teste, e pode encerrar quando quiser.',
  },
  {
    pergunta: 'Serve para o meu tipo de negócio?',
    resposta: 'Serve pra qualquer profissional que agenda atendimentos por horário: salões, barbearias, clínicas de estética, consultórios, personal trainers, psicólogos e outros serviços marcados.',
  },
];

const ConversaoPage = () => {
  const navigate = useNavigate();
  const [faqAberto, setFaqAberto] = useState(null);

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);

  const senhaValida = senha.length >= 8 && /[A-Z]/.test(senha) && /[a-z]/.test(senha) && /[0-9]/.test(senha);

  const handleCadastro = async (e) => {
    e.preventDefault();
    if (!senhaValida) {
      alert('Crie uma senha com pelo menos 8 caracteres, incluindo letra maiúscula, minúscula e número.');
      return;
    }
    setEnviando(true);
    try {
      const resposta = await usuarioService.registrar({ nome, email, senha, telefone });
      if (resposta.status === 201 || resposta.status === 200) {
        alert('Conta criada com sucesso! Faça login pra começar seus 7 dias grátis.');
        navigate('/login');
      }
    } catch (error) {
      alert(error.response?.data?.erro || 'Erro ao criar sua conta. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="bg-white text-slate-900 font-sans overflow-x-hidden">

      {/* BARRA DE OFERTA */}
      <div className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white py-2.5 text-center text-[10px] sm:text-xs font-black uppercase tracking-[0.2em]">
        🎁 {TRIAL_PADRAO.dias} dias grátis — sem cartão de crédito, sem fidelidade
      </div>

      {/* NAV ENXUTA — página de conversão não deve oferecer rotas de fuga */}
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="no-underline">
            <img src="/logo-sincro1.png" alt="Agenda Sincro" className="h-9 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/login" className="hidden sm:block no-underline text-xs font-black text-slate-500 hover:text-[#6D28D9] transition-all uppercase tracking-widest">Entrar</Link>
            <a href="#cadastro" onClick={scrollToCadastro} className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-6 py-3 rounded-2xl text-xs sm:text-sm font-black shadow-lg shadow-purple-200 hover:opacity-90 hover:scale-105 active:scale-95 transition-all uppercase tracking-tighter">
              Criar conta grátis
            </a>
          </div>
        </div>
      </nav>

      {/* HERO + CADASTRO */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden bg-slate-900 bg-[radial-gradient(#6D28D9_1px,transparent_1px)] bg-[size:32px_32px]">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/85 to-slate-900 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center relative z-10">

          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#6D28D9]/15 border border-[#6D28D9]/30 text-purple-300 rounded-2xl text-[10px] font-black uppercase tracking-widest mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-400" />
              </span>
              Assistente de agendamento por WhatsApp
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-[0.95] tracking-tighter mb-8">
              Sua agenda. Seu WhatsApp.<br />
              <span className="bg-gradient-to-r from-[#1883FF] to-[#a855f7] bg-clip-text text-transparent">Zero horário perdido.</span>
            </h1>

            <p className="text-slate-400 text-lg lg:text-xl mb-10 max-w-xl leading-relaxed font-medium">
              Pare de responder "que horário você tem livre?" cem vezes por dia. O Agenda Sincro
              atende, agenda, lembra e cobra sozinho — direto no seu WhatsApp, 24 horas por dia.
            </p>

            <div className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-10">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <p className="text-2xl sm:text-3xl font-black text-white leading-none mb-2">24h</p>
                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-400 leading-tight">Atendendo<br />sozinho</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <p className="text-2xl sm:text-3xl font-black text-white leading-none mb-2">{TRIAL_PADRAO.dias} dias</p>
                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-400 leading-tight">Grátis, sem<br />cartão</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <p className="text-2xl sm:text-3xl font-black text-white leading-none mb-2">5 min</p>
                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-400 leading-tight">Pra sua agenda<br />estar no ar</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-slate-400 text-sm font-bold">
              <ShieldCheck size={20} className="text-emerald-400 shrink-0" />
              Sem taxa de instalação • Sem multa de cancelamento
            </div>
          </div>

          {/* FORMULÁRIO DE CADASTRO */}
          <div id="cadastro" className="relative scroll-mt-24">
            <div className="absolute -inset-6 bg-[#6D28D9]/20 blur-[100px] rounded-full pointer-events-none" />

            <div className="relative bg-white rounded-[2.5rem] p-8 lg:p-10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.6)]">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-[#F5F3FF] border border-purple-100 text-[#6D28D9] px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider mb-5">
                  <Gift size={16} /> {TRIAL_PADRAO.dias} dias grátis
                </div>
                <h2 className="text-3xl font-black tracking-tighter text-slate-900 leading-tight mb-3">
                  Comece agora mesmo
                </h2>
                <p className="text-slate-500 text-sm font-medium leading-relaxed">
                  Sem cartão de crédito. Sem compromisso.<br />
                  Você só decide se vai assinar depois de testar.
                </p>
              </div>

              <form onSubmit={handleCadastro} className="flex flex-col gap-4">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">Seu nome</label>
                  <div className="relative flex items-center">
                    <User size={18} className="absolute left-4 text-slate-400" />
                    <input
                      type="text"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      required
                      placeholder="Ex: João da Silva"
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-slate-200 text-sm font-medium outline-none focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-100 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">Seu melhor e-mail</label>
                  <div className="relative flex items-center">
                    <Mail size={18} className="absolute left-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="voce@seunegocio.com.br"
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-slate-200 text-sm font-medium outline-none focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-100 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">WhatsApp</label>
                  <div className="relative flex items-center">
                    <PhoneIcon size={18} className="absolute left-4 text-slate-400" />
                    <input
                      type="text"
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                      required
                      placeholder="(85) 98888-7766"
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-slate-200 text-sm font-medium outline-none focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-100 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">Crie uma senha</label>
                  <div className="relative flex items-center">
                    <Lock size={18} className="absolute left-4 text-slate-400" />
                    <input
                      type="password"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      placeholder="Mínimo 8 caracteres, maiúscula e número"
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-slate-200 text-sm font-medium outline-none focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-100 transition-all"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={enviando}
                  className="w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white py-5 rounded-2xl font-black text-lg uppercase tracking-tighter shadow-xl shadow-purple-200 hover:opacity-90 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {enviando ? 'Criando conta...' : <>Começar meus {TRIAL_PADRAO.dias} dias grátis <ArrowRight size={20} /></>}
                </button>

                <p className="text-center text-[11px] text-slate-400 font-bold leading-relaxed pt-1 flex items-center justify-center gap-1.5">
                  <Lock size={12} className="shrink-0" />
                  Não pedimos cartão de crédito agora — nem depois do teste, se você não quiser continuar.
                </p>
              </form>

              <p className="text-center text-xs text-slate-500 font-bold mt-6 pt-6 border-t border-slate-100">
                Já tem conta? <Link to="/login" className="text-[#6D28D9] hover:underline">Entrar</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* A DOR */}
      <section className="py-24 bg-[#F8F7FF]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black tracking-tighter uppercase leading-none mb-6">
              O tempo que <span className="text-[#6D28D9]">ninguém coloca na conta</span>
            </h2>
            <p className="text-slate-500 text-lg font-medium max-w-2xl mx-auto">
              Se cada agendamento leva 15 mensagens de ida e volta pra combinar o horário, e você
              marca 10 atendimentos por semana, são <strong className="text-slate-900">mais de 150 mensagens</strong> só
              organizando agenda — toda semana.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-[2.5rem] p-10 border-2 border-slate-200">
              <div className="w-14 h-14 bg-slate-200 text-slate-500 rounded-2xl flex items-center justify-center mb-8">
                <TrendingDown size={28} />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-6 text-slate-400">Sem sistema</h3>
              <ul className="flex flex-col gap-4">
                {[
                  'Você decide todo horário na mão, a qualquer hora do dia',
                  'Cliente esquece o horário e falta sem avisar',
                  'Perde o contato de quem já foi atendido uma vez',
                  'Não sabe quanto faturou sem abrir uma planilha',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm font-bold text-slate-500">
                    <X size={20} className="text-red-400 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 text-white rounded-[2.5rem] p-10 relative overflow-hidden shadow-2xl">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#6D28D9]/30 rounded-full blur-[80px]" />
              <div className="w-14 h-14 bg-gradient-to-br from-[#1883FF] to-[#6D28D9] text-white rounded-2xl flex items-center justify-center mb-8 relative z-10">
                <TrendingUp size={28} />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-6 relative z-10">Com o Agenda Sincro</h3>
              <ul className="flex flex-col gap-4 relative z-10">
                {[
                  'O robô atende, agenda e confirma sozinho, 24 horas por dia',
                  'Lembrete automático antes de cada atendimento reduz as faltas',
                  'Cada cliente fica salvo com histórico, telefone e aniversário',
                  'Financeiro mostra a receita de cada atendimento, em tempo real',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm font-bold text-slate-200">
                    <Check size={20} className="text-emerald-400 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RECURSOS */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-[2px] w-12 bg-[#6D28D9]" />
              <span className="text-[#6D28D9] font-black text-xs uppercase tracking-[0.3em]">Tudo incluso</span>
              <div className="h-[2px] w-12 bg-[#6D28D9]" />
            </div>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase leading-none mb-8">
              Um robô.<br /><span className="text-[#6D28D9]">Toda a sua recepção.</span>
            </h2>
            <p className="text-slate-500 text-lg font-medium leading-relaxed">
              Do primeiro "oi" no WhatsApp até o financeiro do mês, sem digitar a mesma coisa duas vezes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {RECURSOS.map(({ icone: Icone, cor, sombra, titulo, descricao, bullets }) => (
              <div key={titulo} className="bg-[#F8F7FF] p-10 rounded-[2.5rem] transition-all duration-400 hover:-translate-y-2 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] group">
                <div className={`w-14 h-14 bg-gradient-to-br ${cor} text-white rounded-2xl flex items-center justify-center mb-8 shadow-lg ${sombra} group-hover:scale-110 transition-transform`}>
                  <Icone size={26} />
                </div>
                <h3 className="text-lg font-black uppercase tracking-tighter mb-4">{titulo}</h3>
                <p className="text-slate-500 text-sm font-medium leading-relaxed mb-6">{descricao}</p>
                <ul className="flex flex-col gap-2.5">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-xs font-bold text-slate-500">
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-28 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#6D28D9_1px,transparent_1px)] bg-[size:32px_32px] opacity-20 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase leading-none mb-6">
              No ar em <span className="text-purple-400">3 passos</span>
            </h2>
            <p className="text-slate-400 text-lg font-medium">Sem instalação, sem técnico, sem burocracia.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-7xl font-black text-[#6D28D9]/30 leading-none mb-4">01</div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-4">Crie sua conta</h3>
              <p className="text-slate-400 text-sm font-medium leading-relaxed">
                Preencha nome, e-mail e WhatsApp aqui mesmo nesta página. Não pedimos cartão de crédito.
              </p>
            </div>
            <div>
              <div className="text-7xl font-black text-[#6D28D9]/30 leading-none mb-4">02</div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-4">Conecte WhatsApp e Google Agenda</h3>
              <p className="text-slate-400 text-sm font-medium leading-relaxed">
                Leia um QR Code pra conectar seu número e autorize sua agenda em poucos cliques.
              </p>
            </div>
            <div>
              <div className="text-7xl font-black text-[#6D28D9]/30 leading-none mb-4">03</div>
              <h3 className="text-xl font-black uppercase tracking-tighter mb-4">Comece a receber agendamentos</h3>
              <p className="text-slate-400 text-sm font-medium leading-relaxed">
                Compartilhe seu link ou deixe o robô atender no seu WhatsApp. Tudo cai direto na sua agenda.
              </p>
            </div>
          </div>

          <div className="text-center mt-20">
            <a href="#cadastro" onClick={scrollToCadastro} className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-12 py-6 rounded-[2rem] font-black text-xl uppercase tracking-tighter shadow-2xl shadow-purple-900/40 hover:-translate-y-1 hover:opacity-90 transition-all">
              Criar minha conta grátis <ArrowRight size={24} />
            </a>
            <p className="text-slate-500 text-xs font-black uppercase tracking-widest mt-6">
              {TRIAL_PADRAO.dias} dias grátis • sem cartão de crédito
            </p>
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-black tracking-tighter uppercase leading-none mb-6">
              E depois dos <span className="text-[#6D28D9]">{TRIAL_PADRAO.dias} dias?</span>
            </h2>
            <p className="text-slate-500 text-lg font-medium leading-relaxed">
              Só então você escolhe um plano. Se não quiser continuar, é só não assinar —
              não cobramos nada e não existe multa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANOS_VENDAVEIS.map((plano) => {
              const preco = PRECOS[plano.id];
              const destaque = plano.id === 'PREMIUM';
              return (
                <div
                  key={plano.id}
                  className={`p-8 rounded-[2.5rem] flex flex-col relative ${
                    destaque
                      ? 'bg-slate-900 text-white shadow-2xl shadow-purple-900/20 border-4 border-[#6D28D9]'
                      : 'bg-[#F8F7FF] border-2 border-slate-100'
                  }`}
                >
                  {destaque && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-6 py-1.5 rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center gap-1.5">
                      <Star size={12} fill="white" /> {plano.badge}
                    </div>
                  )}
                  <h4 className={`font-black uppercase text-[10px] tracking-widest mb-2 ${destaque ? 'text-purple-300' : 'text-slate-400'}`}>{plano.descricao}</h4>
                  <h3 className={`text-2xl font-black uppercase tracking-tighter mb-6 flex items-center gap-2 ${destaque ? '' : 'text-slate-900'}`}>
                    {plano.nome} {plano.id === 'EMPRESARIAL' && <Building2 size={20} />}
                  </h3>
                  <div className="flex items-baseline gap-2 mb-8">
                    <span className={`text-base font-bold ${destaque ? 'text-slate-400' : 'text-slate-400'}`}>R$</span>
                    <span className="text-5xl font-black">{fmt(preco.mensal)}</span>
                    <span className={`font-bold text-sm ${destaque ? 'text-slate-400' : 'text-slate-400'}`}>/mês</span>
                  </div>
                  <ul className="flex flex-col gap-3 flex-1 mb-8">
                    {BULLETS_CONVERSAO[plano.id].map((bullet) => (
                      <li key={bullet} className={`flex items-center gap-3 text-sm font-bold ${destaque ? 'text-slate-200' : 'text-slate-600'}`}>
                        <Check size={16} className={`shrink-0 ${destaque ? 'text-purple-400' : 'text-[#1883FF]'}`} /> {bullet}
                      </li>
                    ))}
                  </ul>
                  <a href="#cadastro" onClick={scrollToCadastro} className={`block text-center w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all no-underline ${destaque ? 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white hover:opacity-90' : 'bg-slate-900 text-white hover:bg-[#6D28D9]'}`}>
                    Testar grátis
                  </a>
                </div>
              );
            })}
          </div>

          <p className="mt-12 text-center text-slate-500 text-xs font-bold flex flex-wrap items-center justify-center gap-3 uppercase tracking-widest">
            <ShieldCheck size={16} className="text-emerald-500" />
            No plano anual você paga 10 meses e usa 12
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 bg-[#F8F7FF]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl lg:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            Dúvidas <span className="text-[#6D28D9]">frequentes</span>
          </h2>

          <div className="flex flex-col gap-4">
            {FAQS.map((faq, index) => {
              const aberto = faqAberto === index;
              return (
                <div key={faq.pergunta} className={`bg-white border rounded-[2rem] p-8 transition-all ${aberto ? 'border-[#6D28D9]/40' : 'border-slate-100'}`}>
                  <button
                    type="button"
                    onClick={() => setFaqAberto(aberto ? null : index)}
                    className="w-full flex justify-between items-center gap-4 cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    <h4 className="font-black text-lg text-slate-800 tracking-tight m-0">{faq.pergunta}</h4>
                    <Plus size={22} className={`text-slate-300 shrink-0 transition-transform ${aberto ? 'rotate-45' : ''}`} />
                  </button>
                  {aberto && (
                    <p className="text-slate-500 mt-5 text-sm leading-relaxed font-medium">{faq.resposta}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-28 bg-slate-900 bg-[radial-gradient(#6D28D9_1px,transparent_1px)] bg-[size:32px_32px] text-white relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#6D28D9]/15 border border-[#6D28D9]/30 text-purple-300 rounded-2xl text-[10px] font-black uppercase tracking-widest mb-8">
            <Gift size={16} /> {TRIAL_PADRAO.dias} dias grátis, sem cartão
          </div>
          <h2 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-8">
            Cada mensagem respondida na mão<br /><span className="text-purple-400">é um cliente que podia ter marcado sozinho.</span>
          </h2>
          <p className="text-slate-400 text-lg font-medium mb-12 leading-relaxed">
            Você não precisa decidir nada hoje além de testar. Crie sua conta, conecte seu WhatsApp
            e veja sua agenda se organizar sozinha nos próximos {TRIAL_PADRAO.dias} dias.
          </p>
          <a href="#cadastro" onClick={scrollToCadastro} className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-12 py-6 rounded-[2rem] font-black text-xl uppercase tracking-tighter shadow-2xl shadow-purple-900/40 hover:-translate-y-1 hover:opacity-90 transition-all">
            Começar meus {TRIAL_PADRAO.dias} dias grátis <ArrowRight size={24} />
          </a>

          <div className="mt-14 pt-10 border-t border-white/10">
            <p className="text-slate-500 text-xs font-black uppercase tracking-widest mb-5">Prefere falar com uma pessoa antes?</p>
            <a href="https://wa.me/558521807757" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white font-black text-lg hover:text-emerald-400 transition-all no-underline">
              <MessageCircle size={22} /> Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* RODAPÉ */}
      <footer className="bg-[#020617] text-white py-14">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <img src="/logo-sincro1.png" alt="Agenda Sincro" className="h-10 w-auto object-contain" />
          <div className="flex items-center gap-8">
            <a href="https://instagram.com/agendasincro" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-purple-400 transition-all">
              <LuInstagram size={20} />
            </a>
            <Link to="/politica-privacidade" className="no-underline text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-purple-400 transition-all">Privacidade</Link>
            <Link to="/termo-servico" className="no-underline text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-purple-400 transition-all">Termos</Link>
            <Link to="/login" className="no-underline text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-purple-400 transition-all">Entrar</Link>
          </div>
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">© {new Date().getFullYear()} Agenda Sincro</span>
        </div>
      </footer>

      {/* BARRA FIXA MOBILE */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-slate-200 p-4 shadow-[0_-8px_32px_rgba(0,0,0,0.1)]">
        <a href="#cadastro" onClick={scrollToCadastro} className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white py-4 rounded-2xl font-black uppercase tracking-tighter shadow-lg shadow-purple-200 active:scale-95 transition-all no-underline">
          Testar grátis {TRIAL_PADRAO.dias} dias <ArrowRight size={18} />
        </a>
      </div>
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default ConversaoPage;
